import React, { useState } from 'react';
import type { AiReport, AiReportFeedback, ReportDetailLabels } from '../../../shared/types/reports';

export interface AiReportSectionProps {
  aiReport: AiReport | null;
  aiReportFullUrl: string;
  onSubmitFeedback: (feedbackType: 'dislike' | 'like' | 'super_like', comments?: string) => Promise<void>;
  onDeleteFeedback: () => Promise<void>;
  labels: ReportDetailLabels;
}

type FeedbackType = 'dislike' | 'like' | 'super_like';

export function AiReportSection({
  aiReport,
  aiReportFullUrl,
  onSubmitFeedback,
  onDeleteFeedback,
  labels,
}: AiReportSectionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState('');

  if (!aiReport) {
    return null;
  }

  const currentFeedback = aiReport.userFeedback?.feedbackType;

  const handleFeedbackClick = async (type: FeedbackType) => {
    if (isSubmitting) return;

    // If clicking the same feedback, remove it
    if (currentFeedback === type) {
      setIsSubmitting(true);
      try {
        await onDeleteFeedback();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmitFeedback(type, comment || undefined);
      setShowCommentInput(false);
      setComment('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card border-0 shadow mb-4 ai-report-card overflow-hidden" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      {/* Decorative background elements */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '120px',
        height: '120px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-30px',
        left: '20%',
        width: '80px',
        height: '80px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div className="card-body position-relative p-3 p-sm-4">
        {/* Header with icon and button */}
        <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-2 me-sm-3"
              style={{
                width: 40,
                height: 40,
                minWidth: 40,
                minHeight: 40,
                flexShrink: 0,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <i className="bi bi-stars fs-5 text-white"></i>
            </div>
            <div>
              <h6 className="mb-0 text-white fw-bold">{labels.aiInsights}</h6>
              <small className="text-white-50 d-none d-sm-inline">Personalized for you</small>
            </div>
          </div>
          <a
            href={aiReportFullUrl}
            className="btn btn-light btn-sm rounded-pill px-2 px-sm-3 shadow-sm"
            style={{ flexShrink: 0 }}
          >
            <span className="d-none d-sm-inline">{labels.readMore}</span>
            <i className="bi bi-arrow-right ms-sm-1"></i>
          </a>
        </div>
      </div>

      {/* White content area */}
      <div className="bg-white p-3 p-sm-4 pt-3">
        {/* AI Report Content */}
        <div className="mb-3">
          <h6 className="fw-bold mb-2" style={{ color: '#4a5568' }}>
            <i className="bi bi-quote me-1" style={{ color: '#667eea' }}></i>
            {aiReport.title}
          </h6>
          <p className="text-muted mb-0 small">{aiReport.firstSentence}</p>
        </div>

        {/* Feedback Section */}
        <div className="pt-3 border-top">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="text-muted small me-1 d-none d-sm-inline">{labels.feedback.prompt}</span>
            <button
              className={`btn btn-sm rounded-pill px-2 px-sm-3 ${currentFeedback === 'dislike' ? 'btn-danger' : 'btn-outline-secondary'}`}
              onClick={() => handleFeedbackClick('dislike')}
              disabled={isSubmitting}
              title={labels.feedback.dislike}
            >
              <i className="bi bi-hand-thumbs-down"></i>
              <span className="d-none d-sm-inline ms-1">{labels.feedback.dislike}</span>
            </button>
            <button
              className={`btn btn-sm rounded-pill px-2 px-sm-3 ${currentFeedback === 'like' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleFeedbackClick('like')}
              disabled={isSubmitting}
              title={labels.feedback.like}
            >
              <i className="bi bi-hand-thumbs-up"></i>
              <span className="d-none d-sm-inline ms-1">{labels.feedback.like}</span>
            </button>
            <button
              className={`btn btn-sm rounded-pill px-2 px-sm-3 ${currentFeedback === 'super_like' ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => handleFeedbackClick('super_like')}
              disabled={isSubmitting}
              title={labels.feedback.superLike}
            >
              <i className="bi bi-heart"></i>
              <span className="d-none d-sm-inline ms-1">{labels.feedback.superLike}</span>
            </button>
          </div>

          {/* Comment input */}
          {currentFeedback && !showCommentInput && (
            <div className="mt-2">
              <button
                className="btn btn-link btn-sm text-muted p-0"
                onClick={() => setShowCommentInput(true)}
              >
                <i className="bi bi-chat-text me-1"></i>
                {labels.feedback.addComment}
              </button>
            </div>
          )}

          {showCommentInput && (
            <div className="mt-3">
              <textarea
                className="form-control form-control-sm"
                rows={2}
                placeholder={labels.feedback.commentPlaceholder}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="mt-2 d-flex gap-2">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => currentFeedback && handleFeedbackClick(currentFeedback as FeedbackType)}
                  disabled={isSubmitting || !comment.trim()}
                >
                  Save
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setShowCommentInput(false);
                    setComment('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {currentFeedback && aiReport.userFeedback?.comments && (
            <div className="mt-2 small text-muted">
              <i className="bi bi-chat-text me-1"></i>
              {aiReport.userFeedback.comments}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AiReportSection;
